import {
    AI_AGENT_MEETING_ROOT,
    AI_MEETING_AGENT_PAGE_ROUTE_URL,
    SI_ROOT_ROUTE,
    AI_AGENT_MEETING,
    AI_MEETING_AGENT_CHAT_PAGE_ROUTE_URL,
    AI_MEETING_AGENT_LATEST_CHAT_PAGE_ROUTE_URL,
    AI_AGENT_EMAIL,
    AI_AGENT_EMAIL_PAGE_ROUTE_URL,
    HOME_PAGE_ROUTE,
    AI_AGENT_MEETING_LATEST,
    AI_SUPER_AGENT,
    AI_SUPER_AGENT_CHAT_PAGE_ROUTE_URL,
} from "app/pages/sales-intelligence/constants/routes";
import { MEETING_AGENT_SOURCE } from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/constants";
import AIAgentEmailPageContainer from "app/pages/sales-intelligence/pages/ai-agent/containers/AIAgentEmailPageContainer";
import AIAgentMeetingGatePageContainer from "app/pages/sales-intelligence/pages/ai-agent/containers/AIAgentMeetingGatePageContainer";
import { AIAgentMeetingLatestPageContainer } from "app/pages/sales-intelligence/pages/ai-agent/containers/AIAgentMeetingLatestPageContainer";
import AIAgentMeetingPageContainer from "app/pages/sales-intelligence/pages/ai-agent/containers/AIAgentMeetingPageContainer";
import { AISuperAgentPageContainer } from "app/pages/sales-intelligence/pages/ai-agent/containers/AISuperAgentPageContainer";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { fallback } from "app/routes/handlers/redirectFunctions/fallback";
import { redirectPipe } from "app/routes/handlers/redirectFunctions/redirectPipe";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";

const isAiMeetingAgentEnabled = (): boolean => salesSettingsHelper.isAiMeetingAgentEnabled;
const isAiCraftEmailAgentEnabled = (): boolean => salesSettingsHelper.isAiCraftEmailAgentEnabled;
const isSuperAgentEnabled = (): boolean => salesSettingsHelper.isSuperAgentEnabled;

export const aiAgentStateConfig: Record<string, IRouterState> = {
    [AI_AGENT_MEETING_ROOT]: {
        parent: SI_ROOT_ROUTE,
        url: AI_MEETING_AGENT_PAGE_ROUTE_URL,
        component: AIAgentMeetingGatePageContainer,
        configId: "SalesWorkspace",
        packageName: "salesIntelligence",
        pageTitle: "si.pages.ai_agent.document_title",
        trackingId: {
            section: "AI agent",
            subSection: "meeting prep agent",
            subSubSection: "meeting prep agent",
        },

        secondaryBarType: "None",
        redirectTo: redirectPipe(
            () => {
                if (!isAiMeetingAgentEnabled()) {
                    void proNavigator.go(HOME_PAGE_ROUTE);
                }
            },
            fallback({
                workspace: "salesWorkspace",
            }),
        ),
    } as IRouterState,
    [AI_AGENT_MEETING]: {
        parent: SI_ROOT_ROUTE,
        url: AI_MEETING_AGENT_CHAT_PAGE_ROUTE_URL,
        params: {
            id: { dynamic: true },
            isCompanyLevel: { dynamic: true },
            attendee: { dynamic: true },
            isSettingsOpen: { dynamic: true },
            messageId: { dynamic: false },
            pdf: { dynamic: false },
            autogenerate: { dynamic: true },
            source: { dynamic: true },
        },
        component: AIAgentMeetingPageContainer,
        configId: "SalesWorkspace",
        packageName: "salesIntelligence",
        pageTitle: "si.pages.ai_agent.document_title",
        trackingId: (params?: { id?: string; source?: string }) => ({
            section: "AI agent",
            subSection: "meeting prep agent",
            subSubSection: params?.id ? "brief loaded" : "meeting prep form",
            ...(params?.source && { pageCustomData: { source: params.source } }),
        }),

        secondaryBarType: "None",
        redirectTo: redirectPipe(
            () => {
                if (!isAiMeetingAgentEnabled()) {
                    void proNavigator.go(HOME_PAGE_ROUTE);
                }
            },
            fallback({
                workspace: "salesWorkspace",
            }),
        ),
    } as IRouterState,
    [AI_AGENT_MEETING_LATEST]: {
        parent: SI_ROOT_ROUTE,
        url: AI_MEETING_AGENT_LATEST_CHAT_PAGE_ROUTE_URL,
        params: {
            isCompanyLevel: { dynamic: true },
            attendees: { dynamic: true },
            isSettingsOpen: { dynamic: true },
        },
        component: AIAgentMeetingLatestPageContainer,
        configId: "SalesWorkspace",
        packageName: "salesIntelligence",
        pageTitle: "si.pages.ai_agent.document_title",
        trackingId: {
            section: "AI agent",
            subSection: "meeting prep agent",
            subSubSection: "brief loaded",
            pageCustomData: { source: MEETING_AGENT_SOURCE.gCalendar },
        },

        secondaryBarType: "None",
        redirectTo: redirectPipe(
            () => {
                if (!isAiMeetingAgentEnabled()) {
                    void proNavigator.go(HOME_PAGE_ROUTE);
                }
            },
            fallback({
                workspace: "salesWorkspace",
            }),
        ),
    } as IRouterState,
    [AI_AGENT_EMAIL]: {
        parent: SI_ROOT_ROUTE,
        url: AI_AGENT_EMAIL_PAGE_ROUTE_URL,
        params: {
            id: { dynamic: true },
            domain: { dynamic: true },
            recipient: { dynamic: true },
            isSettingsOpen: { dynamic: true },
            autogenerate: { dynamic: true },
        },
        component: AIAgentEmailPageContainer,
        configId: "SalesWorkspace",
        packageName: "salesIntelligence",
        pageTitle: "si.pages.ai_agent_email.document_title",
        trackingId: {
            section: "AI agent",
            subSection: "craft email agent",
            subSubSection: "craft email agent",
        },

        secondaryBarType: "None",
        redirectTo: redirectPipe(
            () => {
                if (!isAiCraftEmailAgentEnabled()) {
                    void proNavigator.go(HOME_PAGE_ROUTE);
                }
            },
            fallback({
                workspace: "salesWorkspace",
            }),
        ),
    } as IRouterState,
    [AI_SUPER_AGENT]: {
        parent: SI_ROOT_ROUTE,
        url: AI_SUPER_AGENT_CHAT_PAGE_ROUTE_URL,
        params: {
            id: { dynamic: true },
            autogenerate: { dynamic: true },
        },
        component: AISuperAgentPageContainer,
        configId: "SalesWorkspace",
        packageName: "salesIntelligence",
        pageTitle: "si.pages.super_agent.document_title",
        trackingId: {
            section: "AI agent",
            subSection: "super agent",
            subSubSection: "chat",
        },

        secondaryBarType: "None",
        redirectTo: redirectPipe(
            () => {
                if (!isSuperAgentEnabled()) {
                    void proNavigator.go(HOME_PAGE_ROUTE);
                }
            },
            fallback({
                workspace: "salesWorkspace",
            }),
        ),
    } as IRouterState,
};
